import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  EmptyState,
  Icon,
  Illustration,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { PaymentLimitsTable } from 'components/PaymentLimits/PaymentLimitsTable'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { LimitLevel, PaymentLimitSheetType } from 'types/paymentLimits'
import { PaymentLimitsFilters } from '../PaymentLimits/components/PaymentLimitsFilters'
import { PaymentLimitSheet } from '../PaymentLimits/components/PaymentLimitSheet'
import { usePaymentLimitsFilters } from '../PaymentLimits/hooks/usePaymentLimitsFilters'

export const MassCustomerLimits = () => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()
  const canEditPaymentLimits = permissions?.paymentLimits?.canEdit ?? false

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)

  const {
    filterProps,
    limits,
    totalElements,
    hasLimits,
    hasActiveFilters,
    isFetching,
    isError,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePaymentLimitsFilters({ level: LimitLevel.ALL_CUSTOMERS })

  const showFilters = hasLimits || hasActiveFilters || isFetching
  const showEmptyState = !isFetching && !isError && !hasLimits && !hasActiveFilters
  const showNoResults = !isFetching && !isError && !hasLimits && hasActiveFilters

  return (
    <>
      <PageHeader
        description={t('massCustomerLimits.pageDescription')}
        {...(canEditPaymentLimits && {
          renderRightSection: (
            <Button
              testId="add-mass-limit-button"
              variant="secondary"
              iconElement={<Icon name="Plus" />}
              onClick={() => setIsAddSheetOpen(true)}
            >
              {t('massCustomerLimits.addLimit')}
            </Button>
          ),
        })}
      />

      <Box paddingB={24}>
        <Callout>{t('massCustomerLimits.callout')}</Callout>
      </Box>

      {showFilters && (
        <Box paddingB={24}>
          <PaymentLimitsFilters {...filterProps} />
        </Box>
      )}

      {isFetching && <RowsSkeletonLoader rows={3} />}

      {hasLimits && (
        <PaymentLimitsTable
          limits={limits}
          tableName="MassCustomerLimitsTable"
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={totalElements}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {showNoResults && (
        <EmptyState
          testId="mass-customer-limits-no-results"
          LottiePlayer={<Illustration name="NoData" />}
          title={t('massCustomerLimits.noResultsTitle', {
            defaultValue: 'No limits match your filters',
          })}
          description={t('massCustomerLimits.noResultsDescription', {
            defaultValue: 'Try adjusting or clearing your filters.',
          })}
        />
      )}

      {showEmptyState && (
        <EmptyState
          testId="mass-customer-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('massCustomerLimits.emptyStateTitle')}
          description={t('massCustomerLimits.emptyStateDescription')}
          {...(canEditPaymentLimits && {
            PrimaryButton: (
              <Button
                variant="secondary"
                iconElement={<Icon name="Plus" />}
                onClick={() => setIsAddSheetOpen(true)}
              >
                {t('massCustomerLimits.addLimit')}
              </Button>
            ),
          })}
        />
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        level={LimitLevel.ALL_CUSTOMERS}
        onClose={() => setIsAddSheetOpen(false)}
      />
    </>
  )
}
